import { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useTourProgress } from '../../hooks/useTourProgress';

export default function GoalsTour() {
    const { t } = useTranslation();
    const { shouldShowTour, completeTour, skipTour } = useTourProgress('goals');
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        if (shouldShowTour) {
            // Small delay to let the page render
            const timer = setTimeout(() => setRun(true), 800);
            return () => clearTimeout(timer);
        }
    }, [shouldShowTour]);

    const steps: Step[] = [
        {
            target: 'body',
            placement: 'center',
            content: (
                <div className="text-center">
                    <h2 className="text-xl font-bold mb-3">{t('tour.goals.welcome.title')}</h2>
                    <p className="text-neutral-600 dark:text-neutral-300">
                        {t('tour.goals.welcome.content')}
                    </p>
                </div>
            ),
            disableBeacon: true,
        },
        {
            target: '[data-tour="add-objective-btn"]',
            content: (
                <div>
                    <h3 className="font-bold text-lg mb-2">{t('tour.goals.addButton.title')}</h3>
                    <p className="text-neutral-600 dark:text-neutral-300 mb-2">
                        {t('tour.goals.addButton.content')}
                    </p>
                    <p className="text-sm text-primary-600 font-medium">
                        👆 {t('tour.goals.addButton.tryIt')}
                    </p>
                </div>
            ),
            placement: 'bottom',
            spotlightClicks: true,
        },
        {
            target: 'body',
            placement: 'center',
            content: (
                <div className="text-center">
                    <h3 className="font-bold text-lg mb-2">{t('tour.goals.objective.title')}</h3>
                    <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                        {t('tour.goals.objective.content')}
                    </p>
                    <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-3">
                        <h4 className="font-bold mb-1">{t('tour.goals.keyResult.title')}</h4>
                        <p className="text-sm text-neutral-600 dark:text-neutral-300">
                            {t('tour.goals.keyResult.content')}
                        </p>
                        <p className="text-xs text-primary-600 mt-2">
                            💡 {t('tour.goals.keyResult.tip')}
                        </p>
                    </div>
                </div>
            ),
        },
    ];

    const handleCallback = (data: CallBackProps) => {
        const { status, index, type } = data;

        if (status === STATUS.FINISHED) {
            setRun(false);
            completeTour();
        } else if (status === STATUS.SKIPPED) {
            setRun(false);
            skipTour();
        }

        if (type === 'step:after') {
            setStepIndex(index + 1);
        }
    };

    if (!shouldShowTour) return null;

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showSkipButton
            showProgress
            scrollToFirstStep
            disableOverlayClose
            callback={handleCallback}
            locale={{
                back: t('tour.buttons.back'),
                close: t('tour.buttons.done'),
                last: t('tour.buttons.done'),
                next: t('tour.buttons.next'),
                skip: t('tour.buttons.skip'),
            }}
            styles={{
                options: {
                    primaryColor: '#6366f1',
                    zIndex: 10000,
                },
                tooltip: {
                    borderRadius: '16px',
                    padding: '20px',
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                buttonNext: {
                    backgroundColor: '#6366f1',
                    borderRadius: '8px',
                    padding: '10px 20px',
                },
                buttonBack: {
                    color: '#6366f1',
                },
                buttonSkip: {
                    color: '#9ca3af',
                },
                spotlight: {
                    borderRadius: '12px',
                },
            }}
        />
    );
}
